import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Typography, Statistic, Table, Tag, Space, Button } from 'antd';
import {
    FileProtectOutlined,
    DollarOutlined,
    WarningOutlined,
    AlertOutlined,
    ArrowRightOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { clientService } from '../../../common/services';
import { ClientAuthContext } from '../../../common/constants';
import Loader from '../../../components/loader/Loader';
import moment from 'moment';
import './Dashboard.scss';

const { Title, Text } = Typography;

export default function ClientDashboard() {
    const navigate = useNavigate();
    const auth = useContext(ClientAuthContext);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadDashboard();
    }, []);

    const loadDashboard = async () => {
        try {
            const resp = await clientService.getDashboard();
            if (resp.success) {
                setData(resp.data);
            }
        } catch (error) {
            toast.error('Erreur lors du chargement du tableau de bord');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <Loader />;
    if (!data) return null;

    const { stats, derniersContrats, derniersSinistres, client } = data;

    const contratColumns = [
        {
            title: 'N. Police',
            dataIndex: 'numeroPolice',
            key: 'police',
            render: (text, record) => (
                <Button type="link" onClick={() => navigate(`/espace-client/contrats/${record._id}`)} style={{ padding: 0 }}>
                    {text}
                </Button>
            ),
        },
        {
            title: 'Compagnie',
            dataIndex: ['compagnie', 'nom'],
            key: 'compagnie',
        },
        {
            title: 'Type',
            dataIndex: ['typeAssurance', 'nom'],
            key: 'type',
        },
        {
            title: 'Echeance',
            dataIndex: 'dateEcheance',
            key: 'echeance',
            render: (d) => moment(d).format('DD/MM/YYYY'),
        },
        {
            title: 'Statut',
            dataIndex: 'statut',
            key: 'statut',
            render: (s) => {
                const colors = { actif: 'green', expire: 'red', resilie: 'default', renouvele: 'blue' };
                return <Tag color={colors[s] || 'default'}>{s}</Tag>;
            },
        },
    ];

    const sinistreColumns = [
        {
            title: 'Contrat',
            dataIndex: ['contrat', 'numeroPolice'],
            key: 'contrat',
        },
        {
            title: 'Type',
            dataIndex: 'typeSinistre',
            key: 'type',
        },
        {
            title: 'Date',
            dataIndex: 'dateSinistre',
            key: 'date',
            render: (d) => moment(d).format('DD/MM/YYYY'),
        },
        {
            title: 'Statut',
            dataIndex: 'statut',
            key: 'statut',
            render: (s) => {
                const colors = { declare: 'orange', en_cours: 'blue', resolu: 'green', rejete: 'red' };
                return <Tag color={colors[s] || 'default'}>{s}</Tag>;
            },
        },
    ];

    const clientNom = client.type === 'entreprise'
        ? client.nom
        : `${client.prenom || ''} ${client.nom}`.trim();

    return (
        <div className="client-dashboard">
            <div className="dashboard-welcome">
                <Title level={3} style={{ margin: 0 }}>
                    Bienvenue, {clientNom}
                </Title>
                <Text type="secondary">Voici le resume de votre espace client</Text>
            </div>

            {/* Stats */}
            <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
                <Col xs={12} sm={6}>
                    <Card className="stat-card">
                        <Statistic
                            title="Contrats actifs"
                            value={stats.contratsActifs}
                            prefix={<FileProtectOutlined style={{ color: '#1A2B5F' }} />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card className="stat-card">
                        <Statistic
                            title="Total contrats"
                            value={stats.totalContrats}
                            prefix={<FileProtectOutlined style={{ color: '#666' }} />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card className="stat-card">
                        <Statistic
                            title="Total verse"
                            value={stats.totalVerse}
                            suffix="FCFA"
                            prefix={<DollarOutlined style={{ color: '#2e7d32' }} />}
                            valueStyle={{ fontSize: 18 }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card className="stat-card">
                        <Statistic
                            title="Solde restant"
                            value={stats.solde}
                            suffix="FCFA"
                            prefix={<WarningOutlined style={{ color: stats.solde > 0 ? '#C8102E' : '#2e7d32' }} />}
                            valueStyle={{ fontSize: 18, color: stats.solde > 0 ? '#C8102E' : '#2e7d32' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Derniers contrats */}
            <Card
                title="Mes derniers contrats"
                extra={
                    <Button type="link" onClick={() => navigate('/espace-client/contrats')}>
                        Voir tout <ArrowRightOutlined />
                    </Button>
                }
                style={{ marginBottom: 24 }}
            >
                <Table
                    dataSource={derniersContrats}
                    columns={contratColumns}
                    rowKey="_id"
                    pagination={false}
                    size="small"
                    scroll={{ x: 600 }}
                />
            </Card>

            {/* Derniers sinistres */}
            {derniersSinistres?.length > 0 && (
                <Card
                    title="Mes derniers sinistres"
                    extra={
                        <Button type="link" onClick={() => navigate('/espace-client/sinistres')}>
                            Voir tout <ArrowRightOutlined />
                        </Button>
                    }
                >
                    <Table
                        dataSource={derniersSinistres}
                        columns={sinistreColumns}
                        rowKey="_id"
                        pagination={false}
                        size="small"
                        scroll={{ x: 500 }}
                    />
                </Card>
            )}

            {/* Actions rapides */}
            <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                <Col xs={24} sm={8}>
                    <Card hoverable className="action-card" onClick={() => navigate('/espace-client/situation')}>
                        <DollarOutlined className="action-icon" />
                        <Text strong>Ma situation financiere</Text>
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card hoverable className="action-card" onClick={() => navigate('/espace-client/sinistres/declarer')}>
                        <AlertOutlined className="action-icon" style={{ color: '#C8102E' }} />
                        <Text strong>Declarer un sinistre</Text>
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card hoverable className="action-card" onClick={() => navigate('/espace-client/contrats')}>
                        <EyeOutlined className="action-icon" style={{ color: '#1A2B5F' }} />
                        <Text strong>Voir mes contrats</Text>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}
